/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import { useConfig } from '@revenuewell/logic-config';
import { useContact } from '@revenuewell/logic-contact';
import { useDesktop } from '@revenuewell/logic-desktop';
import { useNotification } from '@revenuewell/logic-notification';
import { useOidcWithAdmin } from '@revenuewell/logic-oidc';
import { useProduct } from '@revenuewell/logic-product';
import { Call, Device } from '@twilio/voice-sdk';
import React, { createContext, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import AuditService from '../../services/audit-service/audit-service';
import CallService from '../../services/call-service/call-service';
import { Branch, CallHistoryAction } from '../../services/call-service/types';
import NotificationService from '../../services/notification-service/notification-service';
import { normaliseUSPhoneNumber } from '../../utils/formatter';
import { LwpContextProps, LwpState } from './types';
import { mapErrorCodes } from './utils';

export const LwpContext = createContext<LwpContextProps>(null!);

export default function LwpProvider({ children }: PropsWithChildren<{}>) {
  const { config } = useConfig();
  const { hasProduct } = useProduct();
  const { showError } = useNotification();
  const { oidcService, getClaims } = useOidcWithAdmin();
  const { getContactsByPhoneNumber } = useContact();
  const { showSuccess, showError: showErrorNotification } = useNotification();
  const { isDesktopWidget, isDesktopApplication, startLwpCall } = useDesktop();
  const [phoneState, _setPhoneState] = useState<LwpState>({
    phoneStatus: 'offline',
    callStatus: 'nocall'
  });
  const phoneStateRef = React.useRef<LwpState>();
  const callRef = React.useRef<any>();
  const branchRef = React.useRef<Branch>();
  const twilioDeviceRef = React.useRef<Device>();
  const callService = useMemo(() => new CallService(config, oidcService), []);
  const auditService = useMemo(() => new AuditService(config, oidcService), []);
  const notificationService = useMemo(() => new NotificationService(config, oidcService), []);

  useEffect(() => {
    if (!hasProduct('phone') && !hasProduct('phone1')) return;
    if (isDesktopApplication() && !isDesktopWidget) return;

    (async () => {
      const hasMic = await hasMicrophone();
      const hasMicPermission = await hasMicrophonePermission();

      updatePhoneState({ hasMicrophone: hasMic, hasMicrophonePermission: hasMicPermission });

      if (hasMic && hasMicPermission) {
        await createDevice();
      }

      const onDeviceChange = (event: any) => {
        if (event.type === 'devicechange') checkMicrophoneAvailability();
      };

      navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
      return () => navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
    })();
  }, []);

  useEffect(() => {
    console.log('LWP - STATE: ', phoneState);
  }, [phoneState]);

  const createDevice = async () => {
    const claims = await getClaims();
    const branch = await callService.getBranch(claims.locationId);

    if (!branch.phoneCallEnabled) {
      console.log('LWP - brach.phoneCallEnabled is false');
      return;
    }

    branchRef.current = branch;

    const callToken = await callService.getCallToken(claims.locationId);
    const device = new Device(callToken);

    device.on(Device.EventName.Registered, onReady);
    device.on(Device.EventName.Error, onError);
    device.on(Device.EventName.Incoming, onIncoming);
    device.on(Device.EventName.Unregistered, onOffline);
    device.register();

    twilioDeviceRef.current = device;
  };

  const recreateDevice = async () => {
    if (twilioDeviceRef.current) twilioDeviceRef.current.destroy();
    await createDevice();
  };

  const onReady = () => {
    callRef.current = null;
    updatePhoneState({ phoneStatus: 'ready' });
  };

  const onOffline = () => {
    updatePhoneState({ phoneStatus: 'offline', contactName: undefined });
  };

  const onError = (error: any) => {
    console.error('LWP - error: ', error);
    if (error.code === 31205) recreateDevice(); // JWT token expired
    updatePhoneState({ phoneStatus: 'error', error: mapErrorCodes(error.code) });
  };

  const onIncoming = async (call: Call) => {
    const claims = await getClaims();
    const contacts = await getContactsByPhoneNumber(call.parameters.From);
    const parentCallSid = await callService.getParentCallSid(call.parameters.CallSid, claims.locationId);

    callRef.current = call;
    call.on('disconnect', handleCallDisconnect);

    updatePhoneState({
      callSid: call.parameters.CallSid,
      parentCallSid: parentCallSid,
      callStatus: 'initiating',
      callDirection: 'incoming',
      phoneNumber: normaliseUSPhoneNumber(call.parameters.From),
      contactName: contacts && contacts.length > 0 ? `${contacts[0].firstName} ${contacts[0].lastName}` : undefined,
      isMuted: false
    });

    auditService.log({
      branchId: branchRef.current?.id || 0,
      customerUserId: claims.id,
      eventTrackingId: 'CallPhone-IncomingCall'
    });
  };

  const call = async (phoneNumber: string) => {
    const hasMic = await hasMicrophone();
    const hasMicPermission = await hasMicrophonePermission();

    updatePhoneState({ isErrorIgnored: false });

    if (!hasMic) {
      updatePhoneState({ phoneStatus: 'error', error: 'nomicrophone' });
      return;
    }

    if (!hasMicPermission) {
      updatePhoneState({ phoneStatus: 'error', error: 'nomicpermissions' });
      return;
    }

    if (!hasProduct('phone') && !hasProduct('phone1')) {
      updatePhoneState({ phoneStatus: 'error', error: 'noFeatureFlag' });
      return;
    }

    if (!twilioDeviceRef.current) {
      updatePhoneState({ phoneStatus: 'error', error: 'unknown' });
      return;
    }

    const validPhoneNumber = normaliseUSPhoneNumber(phoneNumber);

    if (isDesktopApplication() && !isDesktopWidget) {
      startLwpCall(validPhoneNumber);
    } else {
      const payload = {
        To: validPhoneNumber,
        outboundCallBranchId: branchRef.current?.id.toString() || ''
      };

      updatePhoneState({ phoneNumber: validPhoneNumber, isErrorIgnored: false });
      const call = await twilioDeviceRef.current.connect({ params: payload });

      callRef.current = call;
      call.on('disconnect', handleCallDisconnect);

      updatePhoneState({
        callSid: call.parameters.CallSid,
        callStatus: 'inprogress',
        callDirection: 'outgoing',
        phoneNumber: validPhoneNumber,
        isMuted: call.isMuted()
      });

      await reportCallAction(call, CallHistoryAction.InProgress);

      const contacts = await getContactsByPhoneNumber(validPhoneNumber);
      if (contacts[0]) updatePhoneState({ contactName: `${contacts[0].firstName} ${contacts[0].lastName}` });
    }
  };

  const handleCallDisconnect = async (call: Call) => {
    await reportCallAction(call, CallHistoryAction.Completed);
    await sendNotification(call, 'call-ended');
    resetCallState();
  };

  const resetCallState = () => {
    callRef.current = null;

    updatePhoneState({
      callSid: undefined,
      parentCallSid: undefined,
      callStatus: 'nocall',
      callDirection: undefined,
      contactName: undefined,
      isMuted: false
    });
  };

  const acceptCall = async () => {
    if (callRef.current) {
      if ((twilioDeviceRef.current?.audio?.availableInputDevices?.size ?? 0) > 0) {
        callRef.current.accept();
        updatePhoneState({ callStatus: 'inprogress' });
        await reportCallAction(callRef.current, CallHistoryAction.InProgress);
        await sendNotification(callRef.current, 'call-started');
      } else {
        showError('Please make sure that headset/microphone is connected.');
        callRef.current.disconnect();
      }
    }
  };

  const rejectCall = () => {
    if (callRef.current) {
      callRef.current.reject();
      resetCallState();
    }
  };

  const toggleMuteCall = () => {
    if (callRef.current && phoneState.callStatus === 'inprogress') {
      const isMuted = callRef.current.isMuted();
      callRef.current.mute(!isMuted);
      updatePhoneState({
        isMuted: !isMuted
      });
    }
  };

  const hangupCall = () => {
    if (callRef.current) callRef.current.disconnect();
    if (phoneState.error) updatePhoneState({ isErrorIgnored: true });
  };

  const setPhoneState = (value: LwpState) => {
    phoneStateRef.current = value;
    _setPhoneState(value);
  };

  const updatePhoneState = (value: Partial<LwpState>) => {
    // @ts-ignore
    setPhoneState({ ...phoneStateRef.current, ...value });
  };

  const checkMicrophoneAvailability = async () => {
    const hasMic = await hasMicrophone();
    const hasMicPermission = await hasMicrophonePermission();

    updatePhoneState({ hasMicrophone: hasMic, hasMicrophonePermission: hasMicPermission });

    if (hasMic !== phoneStateRef.current?.hasMicrophone) {
      if (!hasMic) showSuccess('Microphone is connected.');
      else showErrorNotification('Microphone was disconnected.');
    }

    if (!hasMic || !hasMicPermission) {
      if (twilioDeviceRef.current) twilioDeviceRef.current.disconnectAll();
    } else if (hasMic && hasMicPermission && !twilioDeviceRef.current) {
      await createDevice();
    }
  };

  const hasMicrophone = async (): Promise<boolean> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some((d) => d.kind === 'audioinput');
    } catch {
      return false;
    }
  };

  const hasMicrophonePermission = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch {
      return false;
    }
  };

  const reportCallAction = async (call: Call, action: CallHistoryAction) => {
    const claims = await getClaims();

    if (phoneStateRef?.current && branchRef?.current && claims) {
      const callSid = phoneStateRef.current.parentCallSid
        ? phoneStateRef.current.parentCallSid
        : phoneStateRef.current.callSid;

      if (!callSid) return;

      await callService.reportCallAction({
        action,
        customerUserId: claims.id,
        callSid: phoneStateRef.current.parentCallSid
          ? phoneStateRef.current.parentCallSid
          : phoneStateRef.current.callSid || '',
        branchId: branchRef.current.id || 0,
        masterAccountId: parseInt(claims.masterAccountId?.toString() || '0')
      });
    }
  };

  const sendNotification = async (call: Call, type: 'call-started' | 'call-ended' | 'call-missed') => {
    const claims = await getClaims();
    const direction = call.direction === 'OUTGOING' ? 'outbound' : 'inbound';
    const from = direction === 'inbound' ? call.parameters.From : branchRef.current?.phoneNumber;
    const to = direction === 'inbound' ? branchRef.current?.phoneNumber : call.customParameters.get('To');

    const request = {
      locationId: claims.locationId,
      masterAccountId: claims.masterAccountId || 0,
      customerUserId: claims.id,
      type: 'PhoneCall',
      data: {
        to: normaliseUSPhoneNumber(to),
        from: normaliseUSPhoneNumber(from),
        direction: direction,
        source: 'legacy',
        type: type
      }
    };

    notificationService.sendPhoneNotification(request);
  };

  const providerValue = useMemo(
    () => ({
      phoneState,
      call,
      acceptCall,
      rejectCall,
      toggleMuteCall,
      hangupCall
    }),
    [phoneState]
  );

  return <LwpContext.Provider value={providerValue}>{children}</LwpContext.Provider>;
}
